/* istanbul ignore file */
import React, { useRef } from 'react';
import classnames from 'classnames';
import { isEmpty } from 'lodash';
import { DIVIDER } from 'domain/people/employee/performanceReport/usePerformanceReportService';
import { MetricsProps } from 'domain/people/employee/performanceReport/shared/PerformanceHeatmap/performanceHeatmapTypes';
import { Box } from '@mui/material';
import PerformanceHeatmapLabels from 'domain/people/employee/performanceReport/shared/PerformanceHeatmap/PerformanceHeatmapLabels';
import { Alert } from 'framework/ui/mui-builder';
import usePerformanceHeatmap from 'domain/people/employee/performanceReport/shared/PerformanceHeatmap/usePerformanceHeatmap';
import PerformanceHeatmapFixedHeaderDecorator from 'domain/people/employee/performanceReport/shared/PerformanceHeatmap/layout/PerformanceHeatmapFixedHeaderDecorator';

interface PerformanceHeatmapGraphLayoutProps {
  labels: Array<string>;
  currentMonth: string;
  metrics: MetricsProps;
  heatmapHeader: React.ReactNode;
  labelComponent?: React.ReactNode;
  legend: React.ReactNode;
  getItems: (metric: string) => React.ReactNode;
  gridTemplateColumns: string;
  className?: string;
  hasFixedHeader?: boolean;
}

const PerformanceHeatmapGraphLayout = ({
  className,
  heatmapHeader,
  legend,
  getItems,
  gridTemplateColumns,
  labelComponent,
  hasFixedHeader = false,
  ...props
}: PerformanceHeatmapGraphLayoutProps) => {
  const { labels, metrics } = props;
  const { hasRowWithData } = usePerformanceHeatmap(props);
  const scrollableNodeRef = useRef<HTMLDivElement>(null);

  if (isEmpty(metrics)) {
    return (
      <Alert color="info" sx={{ mt: 5 }}>
        No metrics found
      </Alert>
    );
  }

  return (
    <>
      <div className="performance-heatmap__wrapper">
        {labelComponent || <PerformanceHeatmapLabels labels={labels.filter(hasRowWithData)} />}

        <div className="performance-heatmap">
          <div ref={scrollableNodeRef} className="performance-heatmap__scroll">
            <div className="performance-heatmap__inner">
              {hasFixedHeader ? (
                <PerformanceHeatmapFixedHeaderDecorator scrollableNodeRef={scrollableNodeRef}>
                  {heatmapHeader}
                </PerformanceHeatmapFixedHeaderDecorator>
              ) : (
                heatmapHeader
              )}

              {labels.map(metric =>
                hasRowWithData(metric) ? (
                  <Box
                    component="ul"
                    sx={{
                      gridTemplateColumns,
                    }}
                    className={classnames(className, 'performance-heatmap__days', { divider: metric === DIVIDER })}
                  >
                    {getItems(metric)}
                  </Box>
                ) : null,
              )}
              {heatmapHeader}
            </div>
          </div>
        </div>
      </div>
      {legend}
    </>
  );
};

export default PerformanceHeatmapGraphLayout;
